import { z } from 'zod'

/**
 * Form schema for the edit-initiative modal.
 *
 * The funnel fields a person fills in on the way through the gates: success
 * measure, data requirements, result, cost envelope, monitoring and runbook
 * links. The gate panel says which of them the pending gate still wants;
 * this form is where they get typed.
 *
 * What the API owns and this form does not offer:
 *
 *   - **stage** — moves only through a gate. The API returns 400
 *     `STAGE_CHANGE_REQUIRES_GATE` for any attempt, so putting a stage
 *     dropdown here would be an input that exists to be rejected.
 *   - **closure** — written by a Close or Salvage outcome at a gate.
 *   - **fork parent** — declared at create.
 *   - **capability** — rare enough to leave to the agent.
 *   - **the shaping fields** — baseline and its deferral, AI Act class,
 *     governance note, tracker, pilot cohort, disclosure text, target date.
 *     All still on the record, all still writable through
 *     `update_initiative` in chat; they left the form because a person
 *     filling in Gate 1 was scrolling past six fields the conversation had
 *     already established. Dropping them from the schema rather than
 *     hiding the inputs is deliberate: a field validated but not shown can
 *     block a save with an error nobody can see.
 *
 * The form fails fast only on what it can see; everything cross-field is
 * the server's call.
 */

const optionalText = (max: number) =>
  z.preprocess(v => (v === '' ? undefined : v), z.string().max(max).optional())

const optionalUrl = (max: number) =>
  z.preprocess(
    v => (v === '' ? undefined : v),
    z.string().max(max).url({ message: 'Must be a full URL, including https://' }).optional(),
  )

export const editInitiativeSchema = z
  .object({
    initiativeName: z
      .string()
      .trim()
      .min(1, { message: 'Name is required.' })
      .max(200, { message: 'Name must be 200 characters or fewer.' }),
    initiativeDescription: z
      .string()
      .trim()
      .min(1, { message: 'Describe the problem this addresses.' })
      .max(2000, { message: 'Description must be 2000 characters or fewer.' }),
    businessUnitCode: z.string().min(1, { message: 'Pick a business unit.' }),
    ownerObjectId: z.string().uuid({ message: 'Owner must be a valid GUID.' }),

    // Blocked axis — settable at any stage, and the pair has to move
    // together: a note with no reason is a dangling sentence, and the
    // reason is what the board aggregates on.
    blockedReasonCode: z.string().optional(),
    blockedNote: optionalText(400),

    // Gate 1
    successMeasure: optionalText(1000),
    dataRequirements: optionalText(2000),

    // Gate 3
    result: optionalText(1000),
    costEnvelope: optionalText(1000),
    runOwnerConfirmed: z.boolean(),
    confirmGovernanceNote: z.boolean(),

    // Gate 4
    monitoringUrl: optionalUrl(500),
    runbookUrl: optionalUrl(500),
  })
  .superRefine((data, ctx) => {
    if (!data.blockedReasonCode && data.blockedNote) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockedNote'],
        message: 'Pick a blocked reason, or clear the note.',
      })
    }
  })

export type EditInitiativeFormValues = z.input<typeof editInitiativeSchema>
export type EditInitiativeParsed = z.output<typeof editInitiativeSchema>
